import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

export default function ImageGallery({ images, videoUrl, videoUrl2, productName, productBrand }) {
  const altBase = productBrand ? `${productName} — ${productBrand}` : productName;
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const validImages = images.filter(Boolean);
  const videos = [videoUrl, videoUrl2].filter(Boolean);

  // Touch / swipe support
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const prev = () => setActive(i => (i - 1 + validImages.length) % validImages.length);
  const next = () => setActive(i => (i + 1) % validImages.length);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    // Only trigger if horizontal swipe dominates (avoid scroll conflicts)
    if (Math.abs(dx) > 40 && Math.abs(dx) > dy) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (zoomed) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [zoomed]);

  if (validImages.length === 0) return null;

  return (
    <>
      <div className="space-y-3">
        {/* Main image — swipeable */}
        <div
          className="relative aspect-square rounded-xl overflow-hidden bg-gray-50 border border-gray-100 group cursor-zoom-in select-none"
          onClick={() => setZoomed(true)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={active}
              src={validImages[active]}
              alt={active === 0 ? altBase : `${altBase} — vue ${active + 1}`}
              loading={active === 0 ? 'eager' : 'lazy'}
              decoding="async"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2 pointer-events-none"
            />
          </AnimatePresence>

          {/* Nav arrows — visible on desktop hover, always shown on mobile */}
          {validImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow-md md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-white z-10"
                aria-label="Image précédente"
              >
                <ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow-md md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-white z-10"
                aria-label="Image suivante"
              >
                <ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
            </>
          )}

          {/* Zoom hint */}
          <div className="absolute bottom-2 right-2 bg-white/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-3.5 h-3.5 text-gray-600" />
          </div>

          {/* Counter + swipe hint on mobile */}
          {validImages.length > 1 && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
              <div className="bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                {active + 1}/{validImages.length}
              </div>
            </div>
          )}

          {/* Dot indicators (mobile) */}
          {validImages.length > 1 && validImages.length <= 8 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-1.5 md:hidden">
              {validImages.map((_, i) => (
                <div key={i} className={`rounded-full transition-all duration-300 ${i === active ? 'w-4 h-1.5 bg-cs-blue' : 'w-1.5 h-1.5 bg-white/70'}`} />
              ))}
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {validImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {validImages.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  active === i ? 'border-cs-blue shadow-md' : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <img
                  src={img}
                  alt={`${altBase} — miniature ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain p-1"
                />
              </button>
            ))}
          </div>
        )}

        {/* Vidéos (YouTube : lecteur intégré ; sinon fichier vidéo) */}
        {videos.map((videoSrc, vIdx) => {
          const vid = (videoSrc.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/) || [])[1];
          return vid ? (
            <div key={vIdx} className="mt-2 aspect-video rounded-xl overflow-hidden border border-gray-200 bg-black">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${vid}`}
                title={`${productName} — vidéo ${vIdx + 1}`}
                className="w-full h-full"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div key={vIdx} className="mt-2">
              <video
                src={videoSrc}
                controls
                preload="none"
                poster={validImages[0]}
                title={`${productName} — vidéo ${vIdx + 1}`}
                className="w-full rounded-xl border border-gray-200 bg-black"
              />
            </div>
          );
        })}
      </div>

      {/* Lightbox — swipeable */}
      <AnimatePresence>
        {zoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setZoomed(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <button
              onClick={() => setZoomed(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none z-10"
              aria-label="Fermer"
            >×</button>
            {validImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 z-10"
                  aria-label="Image précédente"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 z-10"
                  aria-label="Image suivante"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            <motion.img
              key={active}
              src={validImages[active]}
              alt={active === 0 ? altBase : `${altBase} — vue ${active + 1}`}
              loading="eager"
              decoding="async"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="max-w-full max-h-full object-contain rounded-lg pointer-events-none"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}